import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { renderHTML } from '@agility/nextjs';
import { List, ListItemButton, ListItemIcon } from '@mui/material';
import {
  getIcon,
  getColorByAgilityName,
} from '../../services/agilityTranslations';

const Container = styled('div')({
  display: 'flex',
  flexFlow: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '100%',
});

const VideoContainer = styled('div')({
  '.video': {
    aspectRatio: '16 / 9',
    width: '100%',
  },
  '@media screen and (max-width: 992px)': {
    flexGrow: 0,
    flexShrink: 1,
    width: '100%',
  },
  '@media screen and (min-width: 992px)': {
    flexGrow: 1,
    flexShrink: 0,
    margin: '20px 0',
    maxWidth: '100%',
  },
});

const EmbedContainer = styled('div')(({ $videoSelected }) => ({
  '@media screen and (max-width: 992px)': {
    ...(
      $videoSelected
        ? { width: '450px', height: '300px' }
        : { display: 'none' }
    ),
  },
  '@media screen and (min-width: 992px)': {
    ...(
      $videoSelected
        ? { width: '550px', height: '280px' }
        : { display: 'none' }
    ),
  },
}));

const _Title = styled('div')(({ $titleColor }) => ({
  fontWeight: 600,
  margin: '40px 5px',
  display: 'flex',
  justifyContent: 'center',
  textTransform: 'uppercase',
  '@media screen and (max-width: 768px)': {
    color: $titleColor,
    fontSize: '1.75rem',
    lineHeight: '1.95rem',
    padding: '0 25px',
  },
  '@media screen and (min-width: 768px)': {
    color: $titleColor,
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
  },
}));

const ContentContainer = styled('div')({
  alignSelf: 'flex-start',
  '@media screen and (max-width: 992px)': {
    maxWidth: '100%',
  },
  '@media screen and (min-width: 992px)': {
    margin: '0 10px 0 50px',
    padding: '0 10px',
    maxWidth: '600px',
  },
});

const ListContainer = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  position: 'relative',
  '@media screen and (max-width: 992px)': {
    width: '100%',
    flexDirection: 'column',
  },
  '@media screen and (min-width: 992px)': {
    width: '75%',
    flexDirection: 'row',
  },
});

const ListSeparator = styled('div')({
  margin: '1.5% 0 1.5% 18px',
  width: '95%',
  border: '0.75px dashed #808080',
  '&:last-child': {
    border: '0px',
  },
});

const FormContainer = styled('div')({
  backgroundColor: '#dcebfa',
  borderRadius: '25px',
  '@media screen and (max-width: 992px)': {
    width: '400px',
    height: '500px',
    margin: '15px 5px',
    alignSelf: 'center',
  },
  '@media screen and (min-width: 992px)': {
    width: '400px',
    height: '500px',
    margin: '16px 0 0 0',
  },
});

const _ResourceIcon = styled('div')(({ theme }) => ({
  maxWidth: '80px',
  [theme.breakpoints.up('md')]: {
    padding: '5px 40px 10px 40px',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const _ResourceTitle = styled('div')(({ theme: _theme }) => ({
  margin: '-10px 10px',
}));

const ResourceDescription = styled('div')(({ theme }) => ({
  fontWeight: 300,
  margin: '5px 10px 15px 0',
  fontSize: '0.85rem',
  lineHeight: '0.95rem',
  [theme.breakpoints.up('md')]: {
    padding: '0 25px 0 10px',
  },
  [theme.breakpoints.down('md')]: {
    padding: '0 5px',
  },
}));

const ListText = styled('div')(({ theme: _theme }) => ({
  fontWeight: 500,
  fontSize: '0.95rem',
  lineHeight: '1.10rem',
}));

const _HandoutText = styled('div')(({ theme: _theme }) => ({
  fontWeight: 300,
  fontSize: '0.90rem',
  lineHeight: '1.00rem',
}));

const _Description = styled('div')(({ theme }) => ({
  fontWeight: 300,
  margin: '5px 10px 15px 0',
  fontSize: '0.85rem',
  lineHeight: '0.95rem',
  [theme.breakpoints.up('md')]: {
    padding: '0 25px 0 10px',
  },
  [theme.breakpoints.down('md')]: {
    padding: '0 25px',
  },
}));

const AskTheTrainerWithBullets = ({ module }) => {
  const { fields } = module;

  const [videoSelected, setVideoSelected] = useState(null);

  useEffect(() => {
    if (!videoSelected) {
      if (fields && fields.linkList && fields.linkList.length > 0) {
        setVideoSelected(fields.linkList[0]);
      }
    }
  }, [fields]);

  const [_textAlign, _setTextAlign] = useState('right');

  return (
    <React.Fragment>
      <Container
        style={{
          backgroundColor: getColorByAgilityName(fields.backgroundColor),
        }}
      >
        <ListContainer>
          <ContentContainer>
            <VideoContainer>
              {videoSelected &&
                videoSelected.fields &&
                videoSelected.fields.webinarEmbed && (
                  <EmbedContainer
                    $videoSelected={videoSelected}
                    className="prose max-w-full mx-auto"
                    dangerouslySetInnerHTML={renderHTML(
                      videoSelected.fields.webinarEmbed,
                    )}
                  />
                )}
            </VideoContainer>
            <ResourceDescription
              style={{
                color: fields.textColor,
                margin: 0,
                padding: '-20px 10px',
              }}
            >
              <List>
                {fields.linkList &&
                  fields.linkList.length > 0 &&
                  fields.linkList
                    .sort((a, b) => {
                      if (
                        parseInt(a.fields.sortOrder, 10) >
                        parseInt(b.fields.sortOrder, 10)
                      )
                        return 1;
                      if (
                        parseInt(a.fields.sortOrder, 10) <
                        parseInt(b.fields.sortOrder, 10)
                      )
                        return -1;
                      return 0;
                    })
                    .map((item, index) => {
                      return (
                        <React.Fragment key={index}>
                          <ListItemButton
                            style={{ width: '100%' }}
                            align="left"
                            onClick={(_e) => setVideoSelected(item)}
                          >
                            <ListItemIcon>
                              {getIcon(
                                fields.iconName,
                                fields.iconColor,
                                '1.10rem',
                              )}
                            </ListItemIcon>
                            <ListText>{item.fields.webinarUrl.text}</ListText>
                          </ListItemButton>
                          <ListSeparator />
                        </React.Fragment>
                      );
                    })}
              </List>
            </ResourceDescription>
          </ContentContainer>
          <FormContainer></FormContainer>
        </ListContainer>
      </Container>
    </React.Fragment>
  );
};
AskTheTrainerWithBullets.getCustomInitialProps = async function ({
  agility,
  languageCode,
  _channelName,
}) {
  const api = agility;
  let contentItem = null;
  try {
    let list = await api.getContentList({
      referenceName: 'videoWithBullets',
      locale: languageCode,
      expandAllContentLinks: true,
      take: 1,
    });
    if (list && list.items && list.items.length > 0) {
      contentItem = list;
    } else {
      return null;
    }
  } catch (error) {
    if (console)
      console.error('Could not load Video with Bullets video List.', error);
    return null;
  }

  return {
    fields: contentItem,
  };
};
export default AskTheTrainerWithBullets;
